import React, { useRef } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import AddToDrive from '@mui/icons-material/AddToDrive';
import AdminPanelSettingsOutlined from '@mui/icons-material/AdminPanelSettingsOutlined';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import PhoneIphoneOutlined from '@mui/icons-material/PhoneIphoneOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';

interface SharedActionAccordionNewProps {
  id: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  type?: string;
  expanded: boolean;
  onToggleExpand: (isExpanded: boolean) => void;
  onRemove?: () => void;
  children?: React.ReactNode;
  hasNeedsAttention?: boolean;
  requiredFields?: string[];
}

function formatRequiredFields(fields: string[]) {
  if (fields.length === 1) {
    return fields[0];
  }

  if (fields.length === 2) {
    return `${fields[0]} and ${fields[1]}`;
  }

  return `${fields.slice(0, -1).join(', ')}, and ${fields.at(-1)}`;
}

export default function SharedActionAccordionNew({
  id,
  title,
  icon,
  type,
  expanded,
  onToggleExpand,
  onRemove,
  children,
  hasNeedsAttention,
  requiredFields = [],
}: SharedActionAccordionNewProps) {
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);

  const iconTileColors = {
    backgroundColor:
      type === 'External'
        ? theme.palette.error.light
        : type === 'Policy'
        ? theme.palette.info.light
        : theme.palette.success.light,
    color:
      type === 'External'
        ? theme.palette.error.main
        : type === 'Policy'
        ? theme.palette.info.main
        : theme.palette.success.main,
  };

  const iconStyle = {
    width: 20,
    height: 20,
    color: iconTileColors.color,
  };

  const getActionIcon = (actionType: string): React.ReactNode => {
    switch (actionType) {
      case 'External':
      case 'Gmail Settings':
        return <EmailOutlined sx={iconStyle} />;
      case 'Policy':
        return <DescriptionOutlined sx={iconStyle} />;
      case 'User':
        return <PersonOutlined sx={iconStyle} />;
      case 'Groups':
        return <GroupsOutlined sx={iconStyle} />;
      case 'Calendar':
      case 'Resource Calendar':
        return <CalendarTodayOutlined sx={iconStyle} />;
      case 'Drive':
        return <AddToDrive sx={iconStyle} />;
      case 'Devices':
        return <PhoneIphoneOutlined sx={iconStyle} />;
      case 'Roles':
        return <AdminPanelSettingsOutlined sx={iconStyle} />;
      default:
        return <PersonOutlined sx={iconStyle} />;
    }
  };

  return (
    <Accordion
      id={id}
      ref={ref}
      expanded={expanded}
      onChange={(_, isExpanded) => onToggleExpand(isExpanded)}
      disableGutters
      sx={{
        mb: 2,
        border: `1px solid ${theme.palette.semantic.button.secondary.outline2}`,
        borderRadius: 1,
        overflow: 'hidden',
        boxShadow: 'none',
        bgcolor: 'background.paper',
        '&:before': { display: 'none' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          padding: 1,
          userSelect: 'none',
          gap: 1,
          cursor: children ? 'pointer' : 'default',
        }}
        onClick={() => onToggleExpand(!expanded)}
      >
        {(icon || type) && (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              bgcolor: iconTileColors.backgroundColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {icon
              ? React.isValidElement(icon)
                ? React.cloneElement(
                    icon as React.ReactElement<{ sx?: object }>,
                    {
                      sx: { ...iconStyle, color: iconTileColors.color },
                    }
                  )
                : icon
              : getActionIcon(type || '')}
          </Box>
        )}

        <Box flex={1} sx={{ ml: 1 }}>
          {type && (
            <Typography
              sx={{
                fontSize: 11,
                color: theme.palette.text.secondary,
                lineHeight: 1.2,
              }}
            >
              {type}
            </Typography>
          )}
          <Typography
            sx={{
              mt: 0.5,
              fontWeight: 600,
              fontSize: 13,
              color: theme.palette.text.primary,
            }}
          >
            {title}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center">
          {hasNeedsAttention ? (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.warning.main,
                display: 'inline-flex',
                alignItems: 'center',
                mr: 1,
                whiteSpace: 'nowrap',
              }}
            >
              <WarningAmberOutlined
                sx={{
                  width: 16,
                  height: 16,
                  mr: 0.5,
                }}
              />
              Needs Attention
            </Typography>
          ) : (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                display: 'inline-flex',
                alignItems: 'center',
                mr: 1,
                whiteSpace: 'nowrap',
              }}
            >
              <CheckCircleOutlineIcon sx={{ width: 16, height: 16, mr: 0.5 }} />
              No configuration needed
            </Typography>
          )}

          {children ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(!expanded);
              }}
            >
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: '0.2s',
                }}
              />
            </IconButton>
          ) : null}

          {onRemove && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <AccordionSummary
        expandIcon={null}
        sx={{
          height: 0,
          minHeight: 0,
          padding: 0,
          margin: 0,
          visibility: 'hidden',
        }}
      />
      {children ? (
        <AccordionDetails sx={{ p: 0 }}>
          {hasNeedsAttention && (
            <Box
              sx={{
                px: 1,
                py: 0.75,
                bgcolor:
                  theme.palette.semantic?.surface?.warning || 'warning.light',
                color: theme.palette.semantic?.text?.warning || 'warning.dark',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              <WarningAmberOutlined sx={{ width: 16, height: 16 }} />
              {requiredFields.length
                ? `To save, add ${formatRequiredFields(requiredFields)}.`
                : 'To save, complete the required fields.'}
            </Box>
          )}
          <Box sx={{ px: 3, py: 1.5 }}>{children}</Box>
        </AccordionDetails>
      ) : null}
    </Accordion>
  );
}
